<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { SimpleFourColumnProps } from "./SimpleFourColumnProps";
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";

const props = defineProps<{
    block: PageBlock<SimpleFourColumnProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto">
                <div class="max-w-2xl">
                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :textColor="styleValues.text"
                            :tag="dataValues.title.data.tag"
                            class="text-4xl lg:text-5xl mb-6"
                        />
                    </EditableElement>
                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :textColor="styleValues.text"
                            class="text-lg/8 opacity-70"
                        />
                    </EditableElement>
                </div>

                <div class="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
                    <EditableElement
                        v-for="location in dataValues.locations.data.items"
                        :key="location.id"
                        :blockId="block.id"
                        :field-name="`locations_${location.id}`"
                        :hideable="false"
                    >
                        <div class="text-base/7">
                            <Paragraph
                                :text="location.city"
                                :textColor="styleValues.text"
                                class="pl-6 border-l font-semibold"
                                :style="{
                                    borderColor: styleValues.brand,
                                }"
                            />
                            <div class="pl-6 pt-2">
                                <Paragraph
                                    :text="location.addressFirstLine"
                                    :textColor="styleValues.text"
                                    class="opacity-70"
                                />
                                <Paragraph
                                    :text="location.addressSecondLine"
                                    :textColor="styleValues.text"
                                    class="opacity-70"
                                />
                            </div>
                        </div>
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
